import { useEffect, useRef } from 'react'
import { addListener } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import {
  ADD_ALERT,
  ADD_ALERT_ERROR,
  ADD_ALERT_SUCCESS,
  ADD_MEDIA_FILE,
  ADD_MEDIA_FILE_ERROR,
  ADD_MEDIA_FILE_SUCCESS,
  ADD_PATIENT_ALLERGY,
  ADD_PATIENT_ALLERGY_ERROR,
  ADD_PATIENT_ALLERGY_SUCCESS,
  CHECK_PHONE,
  CHECK_PHONE_ERROR,
  CHECK_PHONE_SUCCESS,
  DELETE_ALERT,
  DELETE_ALERT_ERROR,
  DELETE_ALERT_SUCCESS,
  DELETE_PATIENT_ALLERGY,
  DELETE_PATIENT_ALLERGY_ERROR,
  DELETE_PATIENT_ALLERGY_SUCCESS,
  EDIT_ALERT,
  EDIT_ALERT_ERROR,
  EDIT_ALERT_SUCCESS,
  GET_ALERTS,
  GET_ALERTS_ERROR,
  GET_ALERTS_SUCCESS,
  GET_MEDIA_FILES,
  GET_MEDIA_FILES_ERROR,
  GET_MEDIA_FILES_SUCCESS,
  GET_PATIENT_INTAKE_FORMS,
  GET_PATIENT_INTAKE_FORMS_ERROR,
  GET_PATIENT_INTAKE_FORMS_SUCCESS,
  GET_PATIENT_PAST_VISITS,
  GET_PATIENT_PAST_VISITS_ERROR,
  GET_PATIENT_PAST_VISITS_SUCCESS,
  GET_PATIENT_TREAT_LINK,
  GET_PATIENT_TREAT_LINK_ERROR,
  GET_PATIENT_TREAT_LINK_SUCCESS,
  GET_PATIENT_UPCOMING_VISITS,
  GET_PATIENT_UPCOMING_VISITS_ERROR,
  GET_PATIENT_UPCOMING_VISITS_SUCCESS,
  GET_PATIENTS_CHART,
  GET_PATIENTS_CHART_ERROR,
  GET_PATIENTS_CHART_SUCCESS,
  GET_PRESCRIPTIONS,
  GET_PRESCRIPTIONS_ERROR,
  GET_PRESCRIPTIONS_SUCCESS,
  MARK_ALL_USER_ALERTS_AS_READ,
  MARK_ALL_USER_ALERTS_AS_READ_ERROR,
  MARK_ALL_USER_ALERTS_AS_READ_SUCCESS,
  SAVE_ACCOUNT_INFO,
  SAVE_ACCOUNT_INFO_ERROR,
  SAVE_ACCOUNT_INFO_SUCCESS,
  SAVE_GENERAL_INFO,
  SAVE_GENERAL_INFO_ERROR,
  SAVE_GENERAL_INFO_SUCCESS,
  SET_ERROR,
  SET_MEDIA_FILE_TAGS,
  SET_MEDIA_FILE_TAGS_ERROR,
  SET_MEDIA_FILE_TAGS_SUCCESS,
  SET_PATIENT_HAS_ALLERGIES,
  SET_PATIENT_HAS_ALLERGIES_ERROR,
  SET_PATIENT_HAS_ALLERGIES_SUCCESS,
  SET_PATIENT_HAS_NO_ALLERGIES,
  SET_PATIENT_HAS_NO_ALLERGIES_ERROR,
  SET_PATIENT_HAS_NO_ALLERGIES_SUCCESS,
} from '../../store/charts/ChartsConstants'
import { getAlerts, getPatientChart } from '../../store/charts/ChartsThunk'
import { getTabId } from '../../utils/common'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { changeCurrentPatientAccountInfo } from '../../store/waitingRoom/WaitingRoomActions'
import { deleteAllergyFromStorage, setIsNeedToUpdatedPastVisits } from '../../store/charts/ChartsActions'
import { GET_PRODUCTS, GET_PRODUCTS_ERROR, GET_PRODUCTS_SUCCESS } from '../../store/products/productsConstants'

/**
 * `chartsReducer` sets `patientChartNeedsRefetch` when a save the server already accepted
 * could not be merged into `patientInfo` — no chart is cached for that patient. The
 * listener middleware is created untyped, so `listenerApi.getState()` is `unknown`; narrow
 * it rather than asserting through it.
 */
const getPatientChartNeedingRefetch = (state: unknown): string | null => {
  if (typeof state !== 'object' || state === null || !('chartsReducer' in state)) return null
  const { chartsReducer } = state
  if (typeof chartsReducer !== 'object' || chartsReducer === null) return null
  if (!('patientChartNeedsRefetch' in chartsReducer)) return null
  const { patientChartNeedsRefetch } = chartsReducer
  return typeof patientChartNeedsRefetch === 'string' ? patientChartNeedsRefetch : null
}

export const ChartListener = ({
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  closeGeneralEdit,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  patientId,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  onClearForm,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  deleteAlertsSuccess,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  alertAddedSuccess,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  filesAddedSuccess,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  closeUpload,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  openFiles,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  setLocalLoading,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  setLocalToastErrorMessage,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  updateAccountInfo,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  setIsShowFileCardPopup,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  isNeedToUpdatePastVisits,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  setIsNeedToUpdateLocalPastVisitsList,
  // @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
  setSuccessDeletedAlertId,
}) => {
  const dispatch = useDispatch()
  const isMediaFileOperationRef = useRef(false)

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: () => true,
        effect: async (action, listenerApi) => {
          const currentTabId = getTabId()

          switch (action.type) {
            case GET_PATIENTS_CHART:
            case GET_ALERTS:
            case GET_PRESCRIPTIONS:
            case GET_PATIENT_PAST_VISITS:
            case GET_PATIENT_UPCOMING_VISITS:
            case GET_PATIENT_INTAKE_FORMS:
            case GET_MEDIA_FILES:
            case ADD_PATIENT_ALLERGY:
            case DELETE_PATIENT_ALLERGY:
            case ADD_ALERT:
            case EDIT_ALERT:
            case DELETE_ALERT:
            case SAVE_GENERAL_INFO:
            case SAVE_ACCOUNT_INFO:
            case MARK_ALL_USER_ALERTS_AS_READ:
            case SET_MEDIA_FILE_TAGS:
            case ADD_MEDIA_FILE:
            case GET_PATIENT_TREAT_LINK:
            case CHECK_PHONE:
            case SET_PATIENT_HAS_ALLERGIES:
            case SET_PATIENT_HAS_NO_ALLERGIES:
            case GET_PRODUCTS:
              if (currentTabId === action.extraData.tabId) {
                setLocalLoading(true)
              }
              break
            case GET_PATIENTS_CHART_SUCCESS:
              if (
                action.extraData.customCurrentTabId === action.extraData.tabId &&
                (patientId === undefined || patientId === action.extraData.patientId)
              ) {
                // **Check the internal flag before dispatching getAlerts**
                if (action.payload.AlertSummary.Count > 0 && !isMediaFileOperationRef.current) {
                  setTimeout(() => {
                    // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                    listenerApi.dispatch(getAlerts(action.extraData.patientId, action.extraData.tabId))
                  }, 500)
                }
              }
              setLocalLoading(false)
              isMediaFileOperationRef.current = false // Reset flag after handling
              break

            case GET_PATIENT_PAST_VISITS_SUCCESS:
              if (isNeedToUpdatePastVisits) {
                setIsNeedToUpdateLocalPastVisitsList(true)
                setTimeout(() => {
                  listenerApi.dispatch(setIsNeedToUpdatedPastVisits())
                }, 500)
              }
              setLocalLoading(false)
              break

            case GET_ALERTS_SUCCESS:
            case GET_PRESCRIPTIONS_SUCCESS:
            case GET_PATIENT_UPCOMING_VISITS_SUCCESS:
            case GET_PATIENT_INTAKE_FORMS_SUCCESS:
            case MARK_ALL_USER_ALERTS_AS_READ_SUCCESS:
            case GET_PATIENT_TREAT_LINK_SUCCESS:
            case CHECK_PHONE_SUCCESS:
              setLocalLoading(false)
              break

            case GET_PRESCRIPTIONS_ERROR:
            case GET_PATIENTS_CHART_ERROR:
            case GET_ALERTS_ERROR:
            case GET_PATIENT_PAST_VISITS_ERROR:
            case GET_PATIENT_UPCOMING_VISITS_ERROR:
            case GET_PATIENT_INTAKE_FORMS_ERROR:
            case GET_MEDIA_FILES_ERROR:
            case ADD_PATIENT_ALLERGY_ERROR:
            case DELETE_PATIENT_ALLERGY_ERROR:
            case ADD_ALERT_ERROR:
            case EDIT_ALERT_ERROR:
            case DELETE_ALERT_ERROR:
            case SAVE_GENERAL_INFO_ERROR:
            case SAVE_ACCOUNT_INFO_ERROR:
            case MARK_ALL_USER_ALERTS_AS_READ_ERROR:
            case SET_MEDIA_FILE_TAGS_ERROR:
            case ADD_MEDIA_FILE_ERROR:
            case SET_PATIENT_HAS_NO_ALLERGIES_ERROR:
            case SET_PATIENT_HAS_ALLERGIES_ERROR:
            case SET_PATIENT_HAS_ALLERGIES_SUCCESS:
            case CHECK_PHONE_ERROR:
            case GET_PRODUCTS_SUCCESS:
            case GET_PRODUCTS_ERROR:
              setLocalLoading(false)
              break

            case SAVE_ACCOUNT_INFO_SUCCESS: {
              dispatch(
                changeCurrentPatientAccountInfo({
                  accountInfo: action.extraData.accountInfo,
                  patientId: action.extraData.patientId,
                }),
              )

              // Same as SAVE_GENERAL_INFO_SUCCESS: the save landed on the server but not in
              // the cached chart, so re-fetch rather than close the form over stale values.
              const patientNeedingRefetch = getPatientChartNeedingRefetch(listenerApi.getState())
              if (patientNeedingRefetch) {
                // The action's own tab, not this one: `getPatientChart` gates on
                // `getTabId() === tabId`, so passing `currentTabId` would make that gate pass in
                // every tab the action was synced to and fire one redundant chart fetch per tab.
                // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                listenerApi.dispatch(getPatientChart(patientNeedingRefetch, action.extraData.tabId))
              }

              if (currentTabId === action.extraData.tabId) {
                updateAccountInfo()
              }

              setLocalLoading(false)
              break
            }

            case GET_PATIENT_TREAT_LINK_ERROR:
              setLocalLoading(false)
              if (currentTabId === action.extraData.tabId) {
                if (action.payload.ErrorCode)
                  setLocalToastErrorMessage(getUserFriendlyErrorMessage(action.payload.ErrorCode))
              }
              break
            case SAVE_GENERAL_INFO_SUCCESS: {
              // Closing the edit form tells the clinician the chart now shows what they
              // saved. When the reducer could not apply the save — nothing cached for that
              // patient — that is not yet true, so kick off a chart fetch that will make it
              // true. The close below is deliberately not gated on that fetch: a thunk
              // dispatch here is fire-and-forget, and holding the form open until the
              // network answers would stall the common case on the rare one.
              const patientNeedingRefetch = getPatientChartNeedingRefetch(listenerApi.getState())
              if (patientNeedingRefetch) {
                // The action's own tab, not this one: `getPatientChart` gates on
                // `getTabId() === tabId`, so passing `currentTabId` would make that gate pass in
                // every tab the action was synced to and fire one redundant chart fetch per tab.
                // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                listenerApi.dispatch(getPatientChart(patientNeedingRefetch, action.extraData.tabId))
              }
              if (closeGeneralEdit && currentTabId === action.extraData.tabId) {
                closeGeneralEdit()
              }
              setLocalLoading(false)
              break
            }

            case SET_ERROR:
              if (currentTabId === action.extraData.tabId) {
                if (action?.extraData?.patientId) {
                  setLocalToastErrorMessage(action.payload)
                } else if (action.payload === getUserFriendlyErrorMessage(errorCodes.VISIT_HAS_COMPLETED)) {
                  setLocalToastErrorMessage(action.payload)
                }
              }
              break

            case ADD_PATIENT_ALLERGY_SUCCESS:
              onClearForm()
              setLocalLoading(false)
              break

            case DELETE_PATIENT_ALLERGY_SUCCESS:
              if (currentTabId === action.extraData.tabId) {
                if (patientId === action.extraData.patientId) {
                  setLocalLoading(false)
                  listenerApi.dispatch(deleteAllergyFromStorage(action.extraData.patientId, action.extraData.allergyId))
                }
              }
              break

            case SET_PATIENT_HAS_NO_ALLERGIES_SUCCESS:
              if (currentTabId === action.extraData.tabId) {
                if (patientId === action.extraData.patientId) {
                  setLocalLoading(false)
                  onClearForm()
                }
              }
              break
            case ADD_ALERT_SUCCESS:
            case EDIT_ALERT_SUCCESS:
              if (patientId === action.extraData.patientId) {
                // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                listenerApi.dispatch(getAlerts(action.extraData.patientId, action.extraData.tabId))
                alertAddedSuccess()
              }
              break

            case DELETE_ALERT_SUCCESS:
              setSuccessDeletedAlertId(action.extraData.alertId)
              if (patientId === action.extraData.patientId) {
                // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                listenerApi.dispatch(getAlerts(action.extraData.patientId, action.extraData.tabId))
                deleteAlertsSuccess(action.extraData.alertId)
              }
              break

            case GET_MEDIA_FILES_SUCCESS:
              if (patientId === action.extraData.patientId && currentTabId === action.extraData.tabId) {
                openFiles()
              }
              setLocalLoading(false)
              break

            case SET_MEDIA_FILE_TAGS_SUCCESS:
              if (patientId === action.extraData.patientId) {
                if (currentTabId === action.extraData.tabId) {
                  filesAddedSuccess()
                  setIsShowFileCardPopup(false)
                }
                isMediaFileOperationRef.current = true
                // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                listenerApi.dispatch(getPatientChart(patientId, currentTabId))
              }
              setLocalLoading(false)
              break

            case ADD_MEDIA_FILE_SUCCESS:
              if (patientId === action.extraData.patientId) {
                if (currentTabId === action.extraData.tabId) {
                  closeUpload()
                  if (action.payload.DrugScreen) {
                    isMediaFileOperationRef.current = true
                    // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
                    listenerApi.dispatch(getPatientChart(patientId, currentTabId))
                  }
                }
              }
              setLocalLoading(false)
              break

            default:
              break
          }

          if (action.type === GET_PATIENTS_CHART_SUCCESS) {
            if (isMediaFileOperationRef.current) {
              isMediaFileOperationRef.current = false
            }
          }
        },
      }),
    )

    // @ts-expect-error TS2349 -- MIGRATION: not approved, fix when encountered
    return () => unsubscribe()
  }, [])

  return null
}
